import { useEffect, useMemo, useRef, useState } from "react";
import {
  Copy,
  ExternalLink,
  Check,
  X,
  Terminal,
  Trash2,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { NETWORKS, validateAddress, getMatchedNetworks } from "@/lib/validators";

const HISTORY_KEY = "addr-validator-history-v1";
const HISTORY_LIMIT = 8;

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(items) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

// Aggregate overall input status from per-network results.
function overallStatus(results) {
  const statuses = Object.values(results).map((r) => r.status);
  if (statuses.every((s) => s === "idle")) return "idle";
  if (statuses.some((s) => s === "valid")) return "valid";
  if (statuses.some((s) => s === "incomplete")) return "incomplete";
  return "invalid";
}

const COLOR = {
  idle: { fg: "#666", border: "#1a1a1a", glow: "" },
  valid: { fg: "#00FF41", border: "#00FF41", glow: "shadow-[0_0_24px_rgba(0,255,65,0.18)]" },
  incomplete: { fg: "#FACC15", border: "#FACC15", glow: "shadow-[0_0_24px_rgba(250,204,21,0.18)]" },
  invalid: { fg: "#FF003C", border: "#FF003C", glow: "shadow-[0_0_24px_rgba(255,0,60,0.18)]" },
};

export default function Validator() {
  const [address, setAddress] = useState("");
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);
  const commitTimer = useRef(null);

  useEffect(() => {
    setHistory(loadHistory());
    inputRef.current?.focus();
  }, []);

  const trimmed = address.trim();
  const results = useMemo(() => validateAddress(trimmed), [trimmed]);
  const matched = getMatchedNetworks(results);
  const hasInput = trimmed.length > 0;
  const overall = overallStatus(results);
  const overallColor = COLOR[overall];

  // Commit valid addresses to history after a short pause
  useEffect(() => {
    if (commitTimer.current) clearTimeout(commitTimer.current);
    if (!hasInput || matched.length === 0) return;
    commitTimer.current = setTimeout(() => {
      setHistory((prev) => {
        const filtered = prev.filter((h) => h.address !== trimmed);
        const next = [
          { address: trimmed, networks: matched, ts: Date.now() },
          ...filtered,
        ].slice(0, HISTORY_LIMIT);
        saveHistory(next);
        return next;
      });
    }, 700);
    return () => commitTimer.current && clearTimeout(commitTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, matched.length]);

  const handleCopy = async () => {
    if (!trimmed) return;
    try {
      await navigator.clipboard.writeText(trimmed);
      toast.success("> ADDRESS COPIED TO CLIPBOARD");
    } catch {
      toast.error("> COPY FAILED");
    }
  };

  const handleClear = () => {
    setAddress("");
    inputRef.current?.focus();
  };

  const handleClearHistory = () => {
    setHistory([]);
    saveHistory([]);
    toast.success("> HISTORY PURGED");
  };

  const handleSelectHistory = (addr) => {
    setAddress(addr);
    inputRef.current?.focus();
  };

  const statusLine = useMemo(() => {
    if (!hasInput) return "AWAITING INPUT...";
    if (overall === "valid") return `✓ MATCH FOUND :: ${matched.join(" / ")}`;
    if (overall === "incomplete") return "⚠ INCOMPLETE ADDRESS :: KEEP TYPING...";
    return "✗ NO MATCH :: INVALID ADDRESS FORMAT";
  }, [hasInput, overall, matched]);

  return (
    <div className="relative min-h-screen overflow-hidden" data-testid="page-validator">
      <div className="terminal-grid pointer-events-none fixed inset-0 z-0" />
      <div className="scanlines pointer-events-none fixed inset-0 z-0" />
      <div className="vignette pointer-events-none fixed inset-0 z-0" />

      <div className="relative z-10 mx-auto max-w-6xl px-4 py-10 md:px-8 md:py-16">
        {/* Header */}
        <header className="mb-10 flex flex-col gap-2 md:mb-16">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] text-[#00FF41]/70">
            <Terminal size={14} className="text-[#00FF41]" />
            <span>CRYPTO-OPS</span>
            <span className="ml-auto hidden text-[#444] md:inline">v1.1.0</span>
          </div>
          <h1
            data-testid="title-main"
            className="font-mono text-3xl font-bold uppercase tracking-tighter text-[#f0f0f0] md:text-5xl"
          >
            ADDRESS <span className="text-[#00FF41]">VALIDATOR</span>
            <span className="ml-1 inline-block h-[0.9em] w-2 translate-y-[0.1em] animate-blink bg-[#00FF41]" />
          </h1>
          <p className="max-w-2xl font-mono text-xs tracking-widest text-[#888] md:text-sm">
            // FULL BASE58CHECK + BECH32 / BECH32M VERIFICATION · BTC / TRX / ETH / BSC
          </p>
        </header>

        {/* Command prompt */}
        <section className="mb-6" data-testid="section-input">
          <div
            className={`relative border bg-[#0a0a0a] transition-colors duration-75 ${overallColor.glow}`}
            style={{ borderColor: overallColor.border }}
          >
            <div className="flex items-center gap-3 px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-[#666] border-b border-[#1a1a1a]">
              <span className="h-2 w-2 rounded-full bg-[#FF003C]" />
              <span className="h-2 w-2 rounded-full bg-[#FACC15]" />
              <span className="h-2 w-2 rounded-full bg-[#00FF41]" />
              <span className="ml-2">~/validator/input</span>
              <span className="ml-auto text-[#444]" data-testid="text-charcount">
                {trimmed.length} CHARS
              </span>
            </div>
            <div className="flex items-center gap-3 px-4 py-5 md:px-6 md:py-6">
              <span
                className="select-none font-mono text-lg md:text-2xl"
                style={{ color: overallColor.fg }}
              >
                {">_"}
              </span>
              <input
                ref={inputRef}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="enter wallet address..."
                spellCheck={false}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                data-testid="input-address"
                className="w-full flex-1 border-0 bg-transparent font-mono text-sm tracking-wider outline-none placeholder:text-[#f0f0f0] md:text-lg"
                style={{ color: hasInput ? overallColor.fg : "#f0f0f0" }}
              />
              {hasInput && (
                <span
                  className="hidden font-mono text-2xl md:inline"
                  style={{ color: overallColor.fg }}
                  data-testid="indicator-overall"
                  aria-label={overall}
                >
                  {overall === "valid" ? "✓" : overall === "incomplete" ? "⚠" : "✗"}
                </span>
              )}
              <button
                type="button"
                onClick={handleCopy}
                disabled={!hasInput}
                data-testid="btn-copy"
                title="Copy address"
                className="group flex items-center gap-2 border border-[#1a1a1a] bg-[#050505] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#888] transition-colors duration-75 hover:border-[#00FF41] hover:text-[#00FF41] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#1a1a1a] disabled:hover:text-[#888]"
              >
                <Copy size={12} />
                <span className="hidden md:inline">COPY</span>
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={!hasInput}
                data-testid="btn-clear"
                title="Clear"
                className="group flex items-center gap-2 border border-[#1a1a1a] bg-[#050505] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#888] transition-colors duration-75 hover:border-[#FF003C] hover:text-[#FF003C] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-[#1a1a1a] disabled:hover:text-[#888]"
              >
                <X size={12} />
                <span className="hidden md:inline">CLR</span>
              </button>
            </div>
            <div className="flex items-center gap-2 border-t border-[#1a1a1a] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em] md:px-6">
              <ChevronRight size={12} style={{ color: overallColor.fg }} />
              <span data-testid="text-status" style={{ color: overallColor.fg }}>
                {statusLine}
              </span>
            </div>
          </div>
        </section>

        {/* BTC inspector — shown when input looks BTC-related */}
        {hasInput && results.btc.status !== "idle" && results.btc.type !== null && (
          <BtcInspector btc={results.btc} />
        )}

        {/* Status cards */}
        <section
          className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="section-cards"
        >
          {NETWORKS.map((net) => (
            <NetworkCard
              key={net.id}
              net={net}
              result={results[net.id]}
              address={trimmed}
            />
          ))}
        </section>

        {/* History */}
        <section data-testid="section-history">
          <div className="mb-3 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#1a1a1a]" />
            <h2 className="font-mono text-xs uppercase tracking-[0.35em] text-[#888]">
              // RECENT.LOG
            </h2>
            <div className="h-px flex-1 bg-[#1a1a1a]" />
          </div>
          <div className="border border-[#1a1a1a] bg-[#0a0a0a]">
            <div className="flex items-center justify-between border-b border-[#1a1a1a] px-4 py-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#666]">
                {history.length} ENTRIES
              </span>
              <button
                type="button"
                onClick={handleClearHistory}
                disabled={history.length === 0}
                data-testid="btn-clear-history"
                className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#666] transition-colors hover:text-[#FF003C] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-[#666]"
              >
                <Trash2 size={12} />
                PURGE
              </button>
            </div>
            <div>
              {history.length === 0 ? (
                <div
                  className="px-4 py-8 text-center font-mono text-xs uppercase tracking-[0.3em] text-[#444]"
                  data-testid="text-history-empty"
                >
                  -- NO RECENT VALIDATIONS --
                </div>
              ) : (
                <ul className="divide-y divide-[#1a1a1a]">
                  {history.map((item, idx) => (
                    <li
                      key={`${item.address}-${item.ts}`}
                      data-testid={`history-item-${idx}`}
                    >
                      <button
                        type="button"
                        onClick={() => handleSelectHistory(item.address)}
                        className="group flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-[#0f0f0f]"
                      >
                        <span className="font-mono text-[10px] text-[#444] group-hover:text-[#00FF41]">
                          #{String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="flex-1 truncate font-mono text-xs text-[#bbb] group-hover:text-[#00FF41] md:text-sm">
                          {item.address}
                        </span>
                        <span className="hidden font-mono text-[10px] uppercase tracking-[0.2em] text-[#00FF41] md:inline">
                          [{item.networks.join("/")}]
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        <footer className="mt-12 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#444]">
          <span>// BTC</span>
          <span>// TRX</span>
          <span>// ETH</span>
          <span>// BSC</span>
          <span className="ml-auto">CLIENT-SIDE ONLY · NO DATA LEAVES YOUR DEVICE</span>
        </footer>
      </div>
    </div>
  );
}

function BtcInspector({ btc }) {
  const c = COLOR[btc.status] || COLOR.idle;
  const label =
    btc.status === "valid"
      ? "✓ VALID BITCOIN ADDRESS"
      : btc.status === "incomplete"
        ? "⚠ INCOMPLETE ADDRESS"
        : "✗ INVALID BITCOIN ADDRESS";

  const rows = [
    { k: "TYPE", v: btc.type || "—" },
    { k: "NETWORK", v: btc.network ? btc.network.toUpperCase() : "—" },
    { k: "CHARS", v: String(btc.charCount) },
    { k: "CHECKSUM", v: btc.checksum === "ok" ? "OK ✓" : btc.checksum === "fail" ? "FAIL ✗" : "—" },
  ];

  return (
    <section
      className="mb-6 border bg-[#0a0a0a]"
      style={{ borderColor: c.border }}
      data-testid="btc-inspector"
    >
      <div
        className="flex items-center gap-3 border-b px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
        style={{ borderColor: "#1a1a1a", color: c.fg }}
      >
        <span>BTC.INSPECT</span>
        <span className="ml-auto" data-testid="btc-status-label">
          {label}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4">
        {rows.map((row, i) => (
          <div
            key={row.k}
            className={`px-4 py-3 ${i < rows.length - 1 ? "border-r border-[#1a1a1a]" : ""}`}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#666]">
              {row.k}
            </div>
            <div
              className="mt-1 font-mono text-xs md:text-sm"
              style={{ color: c.fg }}
              data-testid={`btc-${row.k.toLowerCase()}`}
            >
              {row.v}
            </div>
          </div>
        ))}
      </div>
      {btc.message && (
        <div
          className="border-t border-[#1a1a1a] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
          style={{ color: c.fg }}
          data-testid="btc-message"
        >
          ›› {btc.message}
        </div>
      )}
    </section>
  );
}

function NetworkCard({ net, result, address }) {
  const status = result?.status || "idle";
  const c = COLOR[status];
  const isValid = status === "valid";

  const handleExplorer = () => {
    if (!isValid) return;
    window.open(net.explorer(address), "_blank", "noopener,noreferrer");
  };

  const stateLabel =
    status === "valid"
      ? "[ MATCH ✓ ]"
      : status === "incomplete"
        ? "[ INCOMPLETE ⚠ ]"
        : status === "invalid"
          ? "[ NO MATCH ✗ ]"
          : "[ IDLE ]";

  const Icon =
    status === "valid"
      ? Check
      : status === "incomplete"
        ? AlertTriangle
        : status === "invalid"
          ? X
          : null;

  return (
    <div
      data-testid={`card-${net.id}`}
      className={`relative flex flex-col border bg-[#0a0a0a] transition-[border-color,box-shadow] duration-75 ${c.glow}`}
      style={{ borderColor: c.border }}
      data-status={status}
    >
      <div className="flex items-center justify-between border-b border-[#1a1a1a] px-4 py-2">
        <span
          className="font-mono text-[10px] uppercase tracking-[0.3em]"
          style={{ color: c.fg }}
        >
          NET_{net.symbol}
        </span>
        <span
          className="font-mono text-xl leading-none"
          style={{ color: c.fg }}
          data-testid={`status-icon-${net.id}`}
        >
          {Icon ? <Icon size={20} strokeWidth={3} /> : "·"}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-3 px-4 py-5">
        <div>
          <div
            className="font-mono text-2xl font-bold tracking-tight"
            style={{ color: c.fg }}
          >
            {net.symbol}
          </div>
          <div
            className="font-mono text-[10px] uppercase tracking-[0.25em]"
            style={{ color: c.fg }}
          >
            {net.label}
          </div>
        </div>
        <div
          className="font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: c.fg }}
          data-testid={`status-label-${net.id}`}
        >
          {stateLabel}
        </div>
        <button
          type="button"
          onClick={handleExplorer}
          disabled={!isValid}
          data-testid={`btn-explorer-${net.id}`}
          className={`mt-auto flex items-center justify-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-75 ${
            isValid
              ? "border-[#00FF41] bg-transparent text-[#00FF41] hover:bg-[#00FF41] hover:text-[#050505]"
              : "cursor-not-allowed border-[#1a1a1a] text-[#333]"
          }`}
        >
          <ExternalLink size={12} />
          OPEN IN EXPLORER
        </button>
      </div>
    </div>
  );
}
